import type { AppSettings, Category, GridLayout, Taxonomy } from './types'
import { DEFAULT_FLAGS } from './taxonomy'
import { DEFAULT_DAY_START_HOUR } from '../lib/time'

/**
 * Seeded once, on first run, then fully editable from Settings. Nothing outside
 * this file may assume a particular category exists — everything downstream reads
 * the taxonomy, including which categories count as implementation.
 */
export const DEFAULT_CATEGORIES: Category[] = [
  {
    id: 'impl-manual',
    label: 'Manual implementation',
    shortLabel: 'impl',
    color: '#4c8dff',
    flags: [],
    archived: false,
    excludeFromInsights: false,
  },
  {
    id: 'impl-ai',
    label: 'AI implementation',
    shortLabel: 'ai impl',
    color: '#17b8a6',
    flags: [],
    archived: false,
    excludeFromInsights: false,
  },
  {
    id: 'steer-ai',
    label: 'Steering AI',
    shortLabel: 'steer',
    color: '#7bc96f',
    flags: [],
    archived: false,
    excludeFromInsights: false,
  },
  {
    id: 'rework',
    label: 'Rework',
    shortLabel: 'rework',
    color: '#f5a524',
    flags: [],
    archived: false,
    excludeFromInsights: false,
  },
  {
    id: 'review',
    label: 'Review',
    shortLabel: 'review',
    color: '#a78bfa',
    flags: [],
    archived: false,
    excludeFromInsights: false,
  },
  {
    id: 'refine',
    label: 'Refinement',
    shortLabel: 'refine',
    color: '#e879c8',
    flags: [],
    archived: false,
    excludeFromInsights: false,
  },
  {
    id: 'meeting',
    label: 'Meeting',
    shortLabel: 'meet',
    color: '#f2545b',
    flags: [],
    archived: false,
    excludeFromInsights: false,
  },
  {
    id: 'comms',
    label: 'Async comms',
    shortLabel: 'comms',
    color: '#ff8c42',
    flags: [],
    archived: false,
    excludeFromInsights: false,
  },
  {
    id: 'off-break',
    label: 'Break',
    shortLabel: 'break',
    color: '#6b7280',
    flags: [],
    archived: false,
    excludeFromInsights: false,
  },
  {
    id: 'off-personal',
    label: 'Personal',
    shortLabel: 'personal',
    color: '#4b5563',
    flags: [],
    archived: false,
    excludeFromInsights: false,
  },
]

/** Help text for the taxonomy, shown in Settings and the long-press sheet. */
export const CATEGORY_HELP: Record<string, string> = {
  'impl-manual': "First-pass code I'm writing myself.",
  'impl-ai': 'Delegating to an agent: writing prompts, waiting on output.',
  'steer-ai': 'Iterating on agent output to get it to the expected state, before it ships.',
  rework: 'Addressing review comments on my own PR, whoever originally wrote the line.',
  review: "Reviewing other people's PRs, internal and external.",
  refine: 'Story shaping, grooming, spec work.',
  meeting: 'Synchronous and active.',
  comms: 'Async Teams, threads, written back-and-forth.',
  'off-break': 'Short break, still inside the working day.',
  'off-personal': 'Personal time, outside the working day.',
}

export const DEFAULT_LAYOUT: GridLayout = {
  columns: 3,
  rows: 3,
  cells: [
    { id: 'c0', column: 0, row: 0, colSpan: 1, rowSpan: 1, categoryIds: ['impl-manual'] },
    { id: 'c1', column: 1, row: 0, colSpan: 1, rowSpan: 1, categoryIds: ['impl-ai'] },
    { id: 'c2', column: 2, row: 0, colSpan: 1, rowSpan: 1, categoryIds: ['steer-ai'] },
    { id: 'c3', column: 0, row: 1, colSpan: 1, rowSpan: 1, categoryIds: ['rework'] },
    { id: 'c4', column: 1, row: 1, colSpan: 1, rowSpan: 1, categoryIds: ['review'] },
    { id: 'c5', column: 2, row: 1, colSpan: 1, rowSpan: 1, categoryIds: ['refine'] },
    { id: 'c6', column: 0, row: 2, colSpan: 1, rowSpan: 1, categoryIds: ['meeting'] },
    { id: 'c7', column: 1, row: 2, colSpan: 1, rowSpan: 1, categoryIds: ['comms'] },
    { id: 'c8', column: 2, row: 2, colSpan: 1, rowSpan: 1, categoryIds: ['off-break', 'off-personal'] },
  ],
}

export const DEFAULT_TAXONOMY: Taxonomy = {
  categories: DEFAULT_CATEGORIES,
  layout: DEFAULT_LAYOUT,
  flags: DEFAULT_FLAGS,
}

export const DEFAULT_APP_SETTINGS: AppSettings = {
  dayStartHour: DEFAULT_DAY_START_HOUR,
  staleThresholdMinutes: 90,
  theme: 'dark',
  hideWeekends: false,
  workdayStartHour: 9,
  workdayEndHour: 18,
  dailyTargetHours: 8,
}
